/**
 * @module utils/logger
 * @description Sistema de logging condicional con sanitización de datos sensibles.
 *
 * En desarrollo muestra todos los logs. En producción, solo muestra errores y
 * sanitiza automáticamente campos sensibles (email, tokens, API keys, etc.)
 * antes de enviarlos a la consola.
 *
 * ## Comportamiento por entorno
 * | Entorno | `logger.log` | `logger.warn` | `logger.error` | `logger.debug` |
 * |---------|-------------|--------------|---------------|---------------|
 * | Desarrollo | ✅ visible | ✅ visible | ✅ visible | ✅ visible |
 * | Producción | ❌ silenciado | ❌ silenciado | ✅ sanitizado | ❌ silenciado |
 * | Debug mode | ✅ visible | ✅ visible | ✅ visible | ✅ visible |
 *
 * ## Campos sanitizados en producción
 * email, password, token, apiKey, api_key, accessToken, access_token,
 * refreshToken, refresh_token, avatar_url, phone, address, ssn, credit_card
 *
 * ## Uso
 * ```typescript
 * import { logger } from '@/utils/logger';
 * logger.log('[AUTH] Usuario autenticado', { id: user.id });
 * logger.error('[DATA] Error cargando controles', error);
 * ```
 *
 * @see VITE_DEBUG_MODE para activar modo debug en producción
 */

// Detectar entorno de ejecución
const isDev = import.meta.env.DEV;
const isDebug = import.meta.env.VITE_DEBUG_MODE === 'true';

/**
 * Sanitiza argumentos para producción, removiendo información sensible
 *
 * @param args - Array de argumentos a sanitizar
 * @returns Array de argumentos sanitizados
 */
function sanitize(args: unknown[]): unknown[] {
  // En desarrollo o modo debug, no sanitizar
  if (isDev || isDebug) {
    return args;
  }

  // En producción, remover campos sensibles de objetos
  return args.map(arg => {
    if (arg === null || arg === undefined) {
      return arg;
    }

    if (typeof arg === 'object') {
      try {
        // Crear copia para no mutar el original
        const sanitized = Array.isArray(arg) ? [...arg] : { ...arg };

        // Lista de campos sensibles a remover
        const sensitiveFields = [
          'email',
          'password',
          'token',
          'apiKey',
          'api_key',
          'accessToken',
          'access_token',
          'refreshToken',
          'refresh_token',
          'avatar_url',
          'phone',
          'address',
          'ssn',
          'credit_card',
        ];

        // Remover campos sensibles recursivamente
        const removeSensitiveFields = (obj: Record<string, unknown>) => {
          if (typeof obj !== 'object' || obj === null) return;

          Object.keys(obj).forEach(key => {
            if (sensitiveFields.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
              obj[key] = '[REDACTED]';
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
              removeSensitiveFields(obj[key] as Record<string, unknown>);
            }
          });
        };

        removeSensitiveFields(sanitized as Record<string, unknown>);
        return sanitized;
      } catch (error) {
        // Si hay error al sanitizar, devolver mensaje genérico
        return '[Object]';
      }
    }

    return arg;
  });
}

/**
 * Objeto logger con métodos de logging condicional
 */
export const logger = {
  /**
   * Log de información general
   * Solo se muestra en desarrollo o modo debug
   */
  log: (...args: unknown[]) => {
    if (isDev || isDebug) {
      console.log(...args);
    }
  },

  /**
   * Log de advertencias
   * Solo se muestra en desarrollo o modo debug
   */
  warn: (...args: unknown[]) => {
    if (isDev || isDebug) {
      console.warn(...args);
    }
  },

  /**
   * Log de errores
   * SIEMPRE se muestra, pero sanitizado en producción
   */
  error: (...args: unknown[]) => {
    console.error(...sanitize(args));
  },

  /**
   * Log de debug
   * Solo se muestra cuando VITE_DEBUG_MODE=true
   */
  debug: (...args: unknown[]) => {
    if (isDebug) {
      console.log('[DEBUG]', ...args);
    }
  },

  /**
   * Log de información (alias de log)
   */
  info: (...args: unknown[]) => {
    if (isDev || isDebug) {
      console.info(...args);
    }
  },

  /**
   * Log de éxito (con emoji)
   * Solo se muestra en desarrollo o modo debug
   */
  success: (...args: unknown[]) => {
    if (isDev || isDebug) {
      console.log('✅', ...args);
    }
  },
};

/**
 * Utilidad para logging de performance
 * Mide tiempo de ejecución de una función
 */
export function logPerformance<T>(
  label: string,
  fn: () => T | Promise<T>
): T | Promise<T> {
  if (!isDev && !isDebug) {
    // En producción, ejecutar sin logging
    return fn();
  }

  const start = performance.now();
  const result = fn();

  if (result instanceof Promise) {
    return result.finally(() => {
      const end = performance.now();
      logger.debug(`⏱️ [PERFORMANCE] ${label}: ${(end - start).toFixed(2)}ms`);
    }) as T;
  } else {
    const end = performance.now();
    logger.debug(`⏱️ [PERFORMANCE] ${label}: ${(end - start).toFixed(2)}ms`);
    return result;
  }
}

/**
 * Utilidad para crear un logger con prefijo
 * Útil para módulos específicos
 */
export function createLogger(prefix: string) {
  return {
    log: (...args: unknown[]) => logger.log(`[${prefix}]`, ...args),
    warn: (...args: unknown[]) => logger.warn(`[${prefix}]`, ...args),
    error: (...args: unknown[]) => logger.error(`[${prefix}]`, ...args),
    debug: (...args: unknown[]) => logger.debug(`[${prefix}]`, ...args),
    info: (...args: unknown[]) => logger.info(`[${prefix}]`, ...args),
    success: (...args: unknown[]) => logger.success(`[${prefix}]`, ...args),
  };
}

// Exportar estado del entorno para uso externo si es necesario
export const isProduction = !isDev;
export const isDebugMode = isDebug;
